import {
  Field,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { IField } from '@moko/utils';
import { useOptionalOtherInfoContext } from '../OtherInfoContext';
import { useFieldState } from '../useFieldState';
import { FIELD_LABEL_LAYOUT, FIELD_MAX_WIDTH } from '../panelLayout';
import {
  isEmptyFieldValue,
  useFieldReadOnly,
  useReadonlyFieldStyles,
} from './fieldReadOnly';

interface FieldWrapperProps {
  field: IField;
  validationMessage?: string;
  validationState?: 'error' | 'warning' | 'success' | 'none';
  children: React.ReactNode;
  alignCenter?: boolean;
}

const EMPTY_NA_TYPES = new Set([
  'String',
  'Number',
  'Password',
  'Lookup',
  'Date',
  'DateTime',
  'Address',
  'AutoComplete',
  'UserSelector',
  'TenantSelector',
  'Coordinate',
  'NumberWithUnit',
  'Link',
  'Text',
  'Checkbox',
]);

export const ReadOnlyEmpty = () => {
  const styles = useReadonlyFieldStyles();
  return (
    <Text italic className={styles.empty}>
      N/A
    </Text>
  );
};

const useStyles = makeStyles({
  host: {
    width: '100%',
    minWidth: 0,
    maxWidth: `${FIELD_MAX_WIDTH}px`,
    boxSizing: 'border-box',
  },
  fill: {
    maxWidth: '100%',
  },
  horizontal: {
    maxWidth: '100%',
  },
  root: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    alignItems: 'start',
    '& .fui-Field__control': {
      minWidth: 0,
      maxWidth: '100%',
    },
    '& .fui-Input, & .fui-Textarea, & .fui-Dropdown, & .fui-Combobox, & .fui-SpinButton, & .fui-Select, & .fui-DatePicker, & input:not([type="radio"]):not([type="checkbox"]), & textarea':
      {
        width: '100%',
        minWidth: '0 !important',
        maxWidth: '100%',
      },
    '& .fui-Field__label': {
      overflowWrap: 'break-word',
      maxWidth: '100%',
      minWidth: 0,
      marginInline: 0,
    },
    '& .fui-Input:has([aria-invalid="true"]):focus-within, & .fui-Textarea:has([aria-invalid="true"]):focus-within, & .fui-Dropdown[aria-invalid="true"]:focus-within, & .fui-Combobox[aria-invalid="true"]:focus-within':
      {
        borderTopColor: `${tokens.colorPaletteRedBorder2} !important`,
        borderRightColor: `${tokens.colorPaletteRedBorder2} !important`,
        borderBottomColor: `${tokens.colorPaletteRedBorder2} !important`,
        borderLeftColor: `${tokens.colorPaletteRedBorder2} !important`,
      },
    '& .fui-Input:has([aria-invalid="true"]):focus-within::after, & .fui-Textarea:has([aria-invalid="true"]):focus-within::after, & .fui-Dropdown[aria-invalid="true"]:focus-within::after, & .fui-Combobox[aria-invalid="true"]:focus-within::after':
      {
        borderBottomColor: `${tokens.colorPaletteRedBorder2} !important`,
      },
  },
  alignCenter: {
    alignItems: 'center',
  },
});

export const FieldWrapper = ({
  field,
  validationMessage,
  validationState,
  children,
  alignCenter,
}: FieldWrapperProps) => {
  const styles = useStyles();
  const readonlyStyles = useReadonlyFieldStyles();
  const ctx = useOptionalOtherInfoContext();
  const isReadOnly = useFieldReadOnly(field);
  const { fieldValue } = useFieldState(field);
  const orientation = field.labelAbove ? 'vertical' : 'horizontal';
  const fillsPane =
    ctx?.params.mode === 'mobile' || ctx?.params.mode === 'desktop-split';
  const fillsCell =
    Boolean(field.fullWidth) ||
    (field.parentName != null && field.parentName !== 'root');
  const showEmpty =
    isReadOnly &&
    EMPTY_NA_TYPES.has(field.fieldType) &&
    isEmptyFieldValue(fieldValue);

  const fill = fillsPane || fillsCell;

  return (
    <div
      className={mergeClasses(
        styles.host,
        fill && styles.fill,
        orientation === 'horizontal' && styles.horizontal
      )}
    >
      <Field
        id={'field_' + field.name}
        label={field.label}
        hint={field.hint}
        required={Boolean(field.mandatory)}
        orientation={orientation}
        validationMessage={validationMessage}
        validationState={validationState}
        style={orientation === 'horizontal' ? FIELD_LABEL_LAYOUT : undefined}
        className={mergeClasses(
          styles.root,
          alignCenter && styles.alignCenter,
          isReadOnly && readonlyStyles.readOnly
        )}
      >
        {showEmpty ? (
          <div className={readonlyStyles.value}>
            <ReadOnlyEmpty />
          </div>
        ) : (
          children
        )}
      </Field>
    </div>
  );
};
